import { CockpitUser, KeycloakRole, MekUser } from "../api";
import { createAuthClient, getAuthClient } from "./auth0-client";
import { fetchUserProfile } from "../userProfile";
import { extractDevNumber } from "../parse-token";
import { Authorization } from "../Authorization";

export class Auth0Authorization implements Authorization {
    getAccessToken = async (): Promise<string> => {
        return getAuthClient().then((client) =>
            client.getTokenSilently().catch((error) => {
                if (
                    error.error.includes("invalid_grant") &&
                    error.error_description.includes("Unknown or invalid refresh token")
                ) {
                    this.logout();
                }
                return "";
            }),
        );
    };

    getCockpitUser = async (): Promise<CockpitUser> => {
        const token = await this.getAccessToken();
        const mekUser = await this.getUser();
        const crmUser = await fetchUserProfile(token);
        return { mekUser, crmUser };
    };

    getUser = async (): Promise<MekUser | undefined> => {
        const client = await getAuthClient();
        const user = await client.getUser<MekUser>();
        return user
            ? { ...user, fromKeycloak: false, devNumber: extractDevNumber(user), keycloakRoles: [KeycloakRole.Installer,
                    KeycloakRole.ExternalServiceProvider] }
            : undefined;
    };

    loginSilently = async (): Promise<MekUser | undefined> => {
        const client = await getAuthClient();
        const loggedIn = await client.isAuthenticated();
        if (!loggedIn) {
            try {
                await client.handleRedirectCallback();
            } catch (e) {
                return Promise.reject();
            }
        }
        return this.getUser();
    };

    loginWithRedirect = async (): Promise<void> => {
        const client = await getAuthClient();
        return client.loginWithRedirect();
    };

    logout = async (url?: string): Promise<void> => {
        return getAuthClient().then((client) => {
            window.dispatchEvent(new Event("logout"));
            return client.logout({
                logoutParams: { returnTo: url ? url : new URL(window.location.href).origin },
            });
        });
    };

    registerWithRedirect = async (): Promise<void> => {
        const client = await createAuthClient(true);

        return client.loginWithRedirect({
            authorizationParams: { prompt: "login" },
        });
    };

    refreshToken = async (): Promise<string> => {
        return getAuthClient().then((client) =>
            client
                .getTokenSilently({ cacheMode: "off", authorizationParams: { scope: "email profile read:bsr:senec-meinSENEC" } }) // also updates the cache
                .then() // to match return type
                .catch((error) => {
                    if (
                        error.error.includes("invalid_grant") &&
                        error.error_description.includes("Unknown or invalid refresh token")
                    ) {
                        this.logout();
                    }
                    return "";
                }),
        );
    };
}
